import { defineStore } from 'pinia'

interface User {
  employer_full_name?: string
  [key: string]: unknown
}

interface Application {
  [key: string]: unknown
}

interface AmeAccount {
  id: string
  ame_registration_number: string
  ame_district_code: string
  ame_economic_activity: string
  [key: string]: unknown
}

export const useUserStore = defineStore('user', {
  state: () => ({
    user: null as User | null,
    isInitialized: false,
    hasCpswebError: false,
    sessionUserName: null as string | null,
    applications: [] as Application[],
    ameAccounts: [] as AmeAccount[],
    ameNextPage: 1 as number | null,
    ameIsLoading: false,
    sidebarStats: null as Record<string, number> | null,
  }),

  actions: {
    /**
     * Fetches the current user's data from the server.
     * @returns {Promise<void>}
     */
    async fetchUser() {
      try {
        // Tokens are now stored in http-only cookies, so we just try to fetch the user
        // The server will check the cookies automatically
        const { $axios } = useNuxtApp()
        const { data } = await $axios.get('/employers/me')
        this.user = data
      } catch (error: unknown) {
        console.error('Failed to fetch user data', error)

        // Redirect to auth-error page for known auth/account issues
        const err = error as {
          response?: {
            status?: number
            data?: {
              statusCode?: number
              statusMessage?: string
              data?: {
                errors?: Array<{ detail?: string }>
                sessionUserName?: string
              }
            }
          }
        }
        const responseData = err.response?.data
        const is401 = err.response?.status === 401 || responseData?.statusCode === 401
        const is500 = err.response?.status === 500 || responseData?.statusCode === 500
        const isInternalServerError = responseData?.statusMessage === 'Internal Server Error'
        const errorDetails = responseData?.data?.errors ?? []

        // CY Login signed in fine, but the account is not linked to a CPS
        // employer profile — the user is not eligible to use the service.
        // Two backend signals mean the same thing:
        //   - 500 with "cps.employer_individual" (legacy: missing table/link)
        //   - 401 with "User not found" (no employer matched the CY Login id)
        const isNotEligible =
          (is500 &&
            isInternalServerError &&
            errorDetails.some((e) => e.detail?.includes('cps.employer_individual'))) ||
          errorDetails.some((e) => e.detail?.includes('User not found'))

        if (isNotEligible) {
          // Authenticated but not eligible. Keep the flag + name in the store
          // so the eligibility page (/not-eligible) can show the user's name
          // and a sign-out option. The auth middleware routes them there — we
          // deliberately avoid a full-page redirect so this client state
          // survives (the BFF has already cleared the session on the 401).
          this.hasCpswebError = true
          this.sessionUserName = responseData?.data?.sessionUserName ?? null
          this.user = null
        } else {
          this.user = null

          // Genuine sign-in / token failure → the auth-error page.
          if (
            typeof window !== 'undefined' &&
            is401 &&
            isInternalServerError &&
            !window.location.pathname.startsWith('/auth-error') &&
            !window.location.pathname.startsWith('/en/auth-error')
          ) {
            // Preserve locale prefix for English users
            const localePrefix = window.location.pathname.startsWith('/en') ? '/en' : ''
            window.location.href = `${localePrefix}/auth-error`
          }
        }
      } finally {
        this.isInitialized = true
      }
    },

    /**
     * Fetches the sidebar counters (application / draft / appeal counts) and
     * stores them. Call this after actions that change the counts (e.g. a new
     * draft is created or an application is submitted) so the sidebar updates
     * without a full page reload.
     * @returns {Promise<void>}
     */
    async fetchSidebarStats() {
      try {
        const { $axios } = useNuxtApp()
        const { data } = await $axios.get('stats/sidebar-stats')
        this.sidebarStats = data
      } catch (error) {
        console.error('Failed to fetch sidebar stats', error)
      }
    },

    /**
     * Sets the new user data in the store.
     * @param {User} newUserData - The new user data to set.
     */
    setUser(newUserData: User) {
      this.user = newUserData
    },

    /**
     * Logs out the current user via OAuth logout endpoint.
     * @returns {Promise<void>}
     */
    async logOut() {
      try {
        // Call our OAuth logout API
        const response = await $fetch('/bff-api/auth/logout', {
          method: 'POST',
        })

        // Redirect to CY Login end session endpoint to complete logout
        if (response?.endSessionUrl) {
          window.location.href = response.endSessionUrl
        }
      } catch (error) {
        console.error('Logout failed:', error)
      } finally {
        // Clear user state regardless of success or failure
        this.user = null
        this.hasCpswebError = false
        this.sessionUserName = null
      }
    },

    /**
     * Fetches the user's applications.
     * @param {Record<string, unknown>} payload - The fetch applications data.
     * @returns {Promise<{items: Application[], [key: string]: unknown}>} The fetched applications data.
     */
    async getApplications(payload: Record<string, unknown>) {
      const { $axios } = useNuxtApp()
      const { data } = await $axios.get('/applications', { params: payload })
      this.applications = data.items
      return data
    },

    /**
     * Fetches the user's appeals.
     * @param {Record<string, unknown>} payload - The fetch appeals data.
     * @returns {Promise<Record<string, unknown>>} The fetched appeals data.
     */
    async getAppeals(payload: Record<string, unknown>) {
      const { $axios } = useNuxtApp()
      const { data } = await $axios.get('/appeals', { params: payload })
      return data
    },

    /**
     * Withdraws a specific application.
     * @param {string} id - The id of the application to withdraw.
     * @returns {Promise<unknown>} The response data.
     */
    async withdrawApplication(id: string) {
      const { $axios } = useNuxtApp()
      return await $axios.patch(`applications/${id}/withdraw`)
    },

    /**
     * Fetches a specific application's data.
     * @param {string} id - The id of the application to fetch.
     * @returns {Promise<Application>} The fetched application data.
     */
    async getApplication(id: string) {
      const { $axios } = useNuxtApp()
      const { data } = await $axios.get(`/applications/${id}`)
      return data
    },

    /**
     * Fetches a specific appeal's data.
     * @param {string} id - The id of the appeal to fetch.
     * @returns {Promise<Record<string, unknown>>} The fetched appeal data.
     */
    async getAppeal(id: string) {
      const { $axios } = useNuxtApp()
      const { data } = await $axios.get(`/appeals/${id}`)
      return data
    },

    /**
     * Fetches AME accounts with pagination support.
     * Filters out the current user's account to avoid duplicates.
     * @param {AbortSignal} signal - Optional abort signal for cancellation
     * @returns {Promise<void>}
     */
    async fetchAmeAccounts(signal?: AbortSignal) {
      if (!this.ameNextPage) {
        return // No more pages to fetch
      }

      this.ameIsLoading = true
      try {
        const { $axios } = useNuxtApp()
        const { data } = await $axios.get(`/accounts?page=${this.ameNextPage}`, {
          signal,
        })

        // Filter out current user's account
        const filteredItems = data.items.filter((item: AmeAccount) => item.id !== this.user?.id)

        this.ameAccounts = [...this.ameAccounts, ...filteredItems]
        this.ameNextPage = data.pagy.next
      } catch (error: unknown) {
        if (
          error &&
          typeof error === 'object' &&
          'name' in error &&
          (error.name === 'AbortError' || error.name === 'CanceledError')
        ) {
          console.log('Request canceled:', error)
        } else {
          console.error('Failed to fetch AME accounts:', error)
        }
      } finally {
        this.ameIsLoading = false
      }
    },

    /**
     * Resets AME accounts state.
     * Useful when re-fetching from the beginning.
     */
    resetAmeAccounts() {
      this.ameAccounts = []
      this.ameNextPage = 1
      this.ameIsLoading = false
    },
  },
})
